/**
 * Sentry Monitoring Provider
 *
 * Provides error tracking, performance monitoring, and crash reporting through Sentry
 * Implements MonitoringProvider interface for consistent API
 *
 * Works alongside LogRocket:
 * - Sentry: Handles errors, performance, and alerting
 * - LogRocket: Handles session replay and user behavior
 * - Links Sentry errors to LogRocket session URLs for full context
 */

import * as Sentry from '@sentry/react';
import { env, isProduction } from '@/shared/lib/env';
import type {
  MonitoringProvider,
  UserTraits,
  ErrorContext,
  EventProperties,
  BreadcrumbData,
} from '../types';

/**
 * Sentry provider implementation
 */
class SentryProvider implements MonitoringProvider {
  readonly name = 'Sentry';
  private initialized = false;
  private logRocketSessionURL: string | null = null;

  /**
   * Initialize Sentry with configuration
   * Disables Sentry's session replay (LogRocket handles this better)
   * Enables performance monitoring and error tracking
   */
  initialize(): void {
    if (this.initialized) {
      console.warn(`${this.name}: Already initialized`);
      return;
    }

    const dsn = env.VITE_SENTRY_DSN;
    if (!dsn) {
      console.warn(`${this.name}: DSN not configured in environment`);
      return;
    }

    const errorTrackingEnabled = env.VITE_ENABLE_ERROR_TRACKING;
    const performanceMonitoringEnabled = env.VITE_ENABLE_PERFORMANCE_MONITORING;

    if (!errorTrackingEnabled && !performanceMonitoringEnabled) {
      if (!isProduction) {
        console.log(
          `${this.name}: Both error tracking and performance monitoring disabled`
        );
      }
      return;
    }

    try {
      // Determine trace sample rate based on environment
      const tracesSampleRate = this.getTracesSampleRate();

      const integrations = [];

      // Add browser tracing for performance monitoring
      if (performanceMonitoringEnabled) {
        integrations.push(
          Sentry.browserTracingIntegration({
            // Trace all navigation and fetch requests
            traceFetch: true,
            traceXHR: true,
          })
        );
      }

      // DO NOT enable Sentry replay - LogRocket handles session replay better
      // This prevents overlapping functionality and performance issues

      Sentry.init({
        dsn,
        environment: isProduction ? 'production' : 'development',
        integrations,

        // Performance Monitoring
        tracesSampleRate, // Percentage of transactions to trace
        tracePropagationTargets: this.getTracePropagationTargets(),

        // Error Tracking
        enabled: errorTrackingEnabled || performanceMonitoringEnabled,

        // Privacy settings
        sendDefaultPii: env.VITE_SENTRY_SEND_DEFAULT_PII,

        // Release tracking (for source maps and deployment tracking)
        release: env.VITE_APP_VERSION,

        // Normalize depths for error serialization
        normalizeDepth: 10,

        // Maximum breadcrumbs to keep
        maxBreadcrumbs: 50,

        // Attach stack traces to messages
        attachStacktrace: true,
      });

      this.initialized = true;

      const features: string[] = [];
      if (errorTrackingEnabled) features.push('error tracking');
      if (performanceMonitoringEnabled) features.push('performance monitoring');

      if (!isProduction) {
        console.log(
          `${this.name}: Initialized successfully (${features.join(', ')})`
        );
      }
    } catch (error) {
      console.error(`${this.name}: Initialization failed`, error);
    }
  }

  /**
   * Identify the current user
   * Sets user context in Sentry for error attribution
   */
  identifyUser(userId: string, traits?: UserTraits): void {
    if (!this.initialized) return;

    try {
      // Only pass defined values to Sentry.setUser
      const userContext: Sentry.User = { id: userId };

      if (traits?.name) userContext.username = traits.name;
      if (traits?.email) userContext.email = traits.email;

      Sentry.setUser(userContext);

      // Add additional traits as custom context
      if (traits?.role || traits?.organizationId) {
        Sentry.setContext('user_traits', {
          role: traits.role,
          organizationId: traits.organizationId,
        });
      }

      // Add LogRocket session URL if available
      if (this.logRocketSessionURL) {
        Sentry.setContext('logrocket', {
          sessionURL: this.logRocketSessionURL,
        });
      }

      if (!isProduction) {
        console.log(`${this.name}: User identified - ${userId}`);
      }
    } catch (error) {
      console.error(`${this.name}: Failed to identify user`, error);
    }
  }

  /**
   * Capture and report an error
   * Includes custom context, tags, and LogRocket session URL
   */
  captureError(error: Error, context?: ErrorContext): void {
    if (!this.initialized) return;

    try {
      // Set scope for this error
      Sentry.withScope((scope) => {
        // Set error level
        if (context?.level) {
          scope.setLevel(context.level);
        }

        // Add tags for filtering in Sentry
        if (context?.tags) {
          for (const [key, value] of Object.entries(context.tags)) {
            scope.setTag(key, value);
          }
        }

        // Add extra context data
        if (context?.extra) {
          for (const [key, value] of Object.entries(context.extra)) {
            scope.setExtra(key, value);
          }
        }

        // Set fingerprint for error grouping
        if (context?.fingerprint) {
          scope.setFingerprint(context.fingerprint as string[]);
        }

        // Add LogRocket session URL to error context
        if (this.logRocketSessionURL) {
          scope.setContext('logrocket', {
            sessionURL: this.logRocketSessionURL,
          });
        }

        // Capture the error
        Sentry.captureException(error);
      });
    } catch (captureError) {
      console.error(`${this.name}: Failed to capture error`, captureError);
    }
  }

  /**
   * Track a custom event
   * Events are primarily for analytics (LogRocket), Sentry only gets breadcrumbs for error context
   * This prevents noise in Sentry while maintaining debugging context
   */
  trackEvent(event: string, properties?: EventProperties): void {
    if (!this.initialized) return;

    try {
      // Only add as breadcrumb for error context - do NOT capture as message
      // Events are analytics and belong in LogRocket, not Sentry
      Sentry.addBreadcrumb({
        category: 'user-action',
        message: event,
        level: 'info',
        data: properties as Record<string, unknown>,
      });
    } catch (error) {
      console.error(`${this.name}: Failed to track event`, error);
    }
  }

  /**
   * Add a breadcrumb for debugging
   * Breadcrumbs appear in error reports showing user actions before the error
   */
  addBreadcrumb(message: string, data?: BreadcrumbData): void {
    if (!this.initialized) return;

    try {
      Sentry.addBreadcrumb({
        message,
        level: 'info',
        data: data as Record<string, unknown>,
      });
    } catch (error) {
      console.error(`${this.name}: Failed to add breadcrumb`, error);
    }
  }

  /**
   * Set custom data for the current session
   * Adds context that appears in all future errors
   */
  setCustomData(key: string, value: unknown): void {
    if (!this.initialized) return;

    try {
      Sentry.setContext(key, value as Record<string, unknown>);
    } catch (error) {
      console.error(`${this.name}: Failed to set custom data`, error);
    }
  }

  /**
   * Clear user identification
   * Called on logout to prevent user data leakage
   */
  clearUser(): void {
    if (!this.initialized) return;

    try {
      Sentry.setUser(null);
      this.logRocketSessionURL = null;
      if (!isProduction) {
        console.log(`${this.name}: User cleared`);
      }
    } catch (error) {
      console.error(`${this.name}: Failed to clear user`, error);
    }
  }

  /**
   * Set LogRocket session URL for linking
   * Called by LogRocket provider after session starts
   *
   * @param url - LogRocket session URL
   */
  setLogRocketSession(url: string): void {
    if (!this.initialized) return;

    this.logRocketSessionURL = url;

    try {
      Sentry.setContext('logrocket', {
        sessionURL: url,
      });
      if (!isProduction) {
        console.log(`${this.name}: LogRocket session linked`);
      }
    } catch (error) {
      console.error(`${this.name}: Failed to link LogRocket session`, error);
    }
  }

  /**
   * Get traces sample rate based on environment
   * Production: 10% (cost-effective)
   * Development: 100% (full debugging)
   */
  private getTracesSampleRate(): number {
    const envRate = env.VITE_SENTRY_TRACES_SAMPLE_RATE;
    if (envRate !== undefined) {
      return envRate;
    }

    // Default rates
    return isProduction ? 0.1 : 1.0;
  }

  /**
   * Get trace propagation targets
   * Controls which requests get distributed tracing headers.
   *
   * Scoped to the configured API base URL only — never the bare `localhost`
   * wildcard, otherwise Sentry would inject `sentry-trace` / `baggage`
   * headers into the P2P chat signaling endpoints (`/turn-creds`,
   * `/signal/*`) and trip CORS preflight, plus violate the
   * "no Sentry calls in p2p-chat" invariant.
   */
  private getTracePropagationTargets(): Array<string | RegExp> {
    const apiUrl = env.VITE_API_BASE_URL;
    const targets: Array<string | RegExp> = [];

    if (apiUrl) {
      try {
        const url = new URL(apiUrl);
        targets.push(url.origin, new RegExp(`^${url.origin}`));
      } catch (error) {
        console.warn(`${this.name}: Invalid API URL for tracing`, error);
      }
    }

    return targets;
  }
}

/**
 * Export singleton instance
 */
export const sentryProvider = new SentryProvider();
